import { useCallback, useEffect, useRef, useState } from 'react';

import { useGetBoxes } from '../api/getBoxes';
import { IBox } from '../models/box';

interface IUseSearchBoxViewResult {
  searchResults: IBox[];
  search: (term: string) => void;
  isLoading: boolean;
  isError: boolean;
  hasSearched: boolean;
  clearResults: () => void;
}

export const useSearchBoxView = (): IUseSearchBoxViewResult => {
  const [searchTerm, setSearchTerm] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [triggerSearch, setTriggerSearch] = useState(false);
  const prevFetchStatus = useRef<string>('idle');

  const { data, isLoading, isError, refetch, fetchStatus } = useGetBoxes({
    params: { thingName: searchTerm },
    enabled: false,
  });

  useEffect(() => {
    if (triggerSearch && searchTerm) {
      refetch();
      setTriggerSearch(false);
    }
  }, [triggerSearch, searchTerm, refetch]);

  useEffect(() => {
    if (prevFetchStatus.current === 'fetching' && fetchStatus === 'idle') {
      setHasSearched(true);
    }
    prevFetchStatus.current = fetchStatus;
  }, [fetchStatus]);

  const search = useCallback((term: string) => {
    setSearchTerm(term);
    setHasSearched(false);
    if (term) {
      setTriggerSearch(true);
    }
  }, []);

  const clearResults = useCallback(() => {
    setSearchTerm('');
    setHasSearched(false);
  }, []);

  return {
    searchResults: data?.boxes || [],
    search,
    isLoading,
    isError,
    hasSearched,
    clearResults,
  };
};
